<template>
  <div class="controls">
    <div class="controls__start experiment-header">
      <div class="d-flex justify-content-between align-items-center">
        <span id="experiment-header-title" class="controls__count">{{ currentExperiment ? currentExperiment.name : '' }}</span>
        <EncodedIdField
          :id="currentExperiment?.id"
          :type="'experiment'"
          :hideLabel="true"
          class="ml-2"
        />
      </div>
      <span v-if="isLoading && training_sessions && training_sessions.length > 0" class="controls__count">Training Sessions</span>
      <span v-else class="controls__count">{{ `Training Sessions (${training_sessions.length})` }}</span>
    </div>
    <div class="controls__middle">
      <HelpTip class="ml-2">
        Training session list displays both ongoing and previous sessions associated with a specific experiment.
        You can view details, training metrics (charts), logs, and models by selecting any session card.
        To compare all sessions within the experiment, click on "ALL CHARTS".
      </HelpTip>

      <HelpTip v-if="permissions && !permissions.trainer_write" class="ml-2" help_style="alert">
        This is a read only experiment. Editing functionalities are disabled.
      </HelpTip>
    </div>

    <div class="controls__end">
      <div class="controls__actions">
        <button
          v-if="permissions && (permissions.trainer_read || permissions.trainer_write)"
          id="all-charts-btn"
          class="button"
          @click="handleShowAllChartsClick()"
        >
          <SVGIcon
            :iconName="'analytics'"
            :width="'18px'"
            :height="'18px'"
          />All Charts
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          id="new-session-btn"
          class="button highlight"
          @click="openTrainerStartModal(null)"
        >
          New Session
        </button>
      </div>
    </div>
  </div>
  <div v-if="isLoading" class="empty-state">
    <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  </div>
  <div v-else-if="!isLoading && training_sessions.length === 0" class="empty-state">
    <EmptyState
      :title="'No training sessions found for this experiment'"
      :subtitle="'To add a new training session, click on NEW SESSION'"
    />
  </div>
  <template v-else-if="!isLoading && training_sessions.length > 0">
    <div class="trainers-card-container scrollbar">
      <TrainerCard
        :training_sessions="training_sessions"
        :currentProject="currentProject"
        :permissions="permissions"
        @info-clicked="(session) => handleInfoClicked(session)"
        @delete-session="openConfirmDeleteSessionModal"
        @terminate-session="openConfirmStopTrainingSessionModal"
        @delete-trainer="openConfirmDeleteTrainerModal(t)"
        @charts-clicked="(session) =>handleSessionChartsClicked(session)"
        @refresh-session="(session) => handleRefreshSession(session)"
        @clone-training-session="(session) => openTrainerStartModal(session)"
        @resume-training-session="(session) => openConfirmResumeSessionModal(session)"
      />
    </div>
  </template>

  <CreateTrainerSessionModal
    ref="startTrainerSessionModal"
    :trainer="trainerToCreateSession"
    :experiments="experiments"
    :trainerSession="sourceTrainingSession"
    @create-trainer-session="handleTrainingSessionCreated(trainerToCreateSession)"
  />
  <TrainingSessionDetailsModal
    ref="trainingSessionDetailsModal"
    :session="selectedTrainingSession"
    :permissions="permissions"
    @stop-training="openConfirmStopTrainingSessionModal"
    @delete-session="openConfirmDeleteSessionModal"
  />
  <TrainingSessionAnalyticsModal
    ref="trainingSessionAnalyticsModal"
    :trainerSession="trainerToShowAnalytics"
    :projectID="currentProject ? currentProject.id : 0"
  />
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import TrainerCard from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainerCardV2.vue';
import TrainingSessionDetailsModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingSessionDetailsModalV2.vue';
import TrainingSessionAnalyticsModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingSessionAnalyticsModal.vue';
import CreateTrainerSessionModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/CreateTrainerSessionModalV2.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import EmptyState from '@/components/EmptyState.vue';
import HelpTip from '../../HelpTip.vue';

const $toast = useToast();

export default {
  name: "TrainerSessionManagement",
  components: {
    TrainerCard,
    CreateTrainerSessionModal,
    TrainingSessionDetailsModal,
    TrainingSessionAnalyticsModal,
    ConfirmModal,
    EncodedIdField,
    SVGIcon,
    HelpTip,
    EmptyState,
  },
  inject: {
    experiments: { default: null },
    isPublic: { default: false },
  },
  props: {
  },
  emits: [
    'trainer-set-created', 'update-list',
  ],
  data() {
    return {
      dataConnect: null,
      isLoading: true,
      training_sessions: [],
      trainingSessionList: [],
      trainerToCreateSession: null,
      trainerToShowAnalytics: null,
      loadingFinished: true,
      selectedTrainingSession: null,
      selectedTrainingExperiment: null,
      selectedTrainerDetails: null,
      interval: null,
      confirmMessage: null,
      confirmMessageHeader: null,
      toDelete: null,
      confirmFunction: null,
      buttonText: 'Delete',
      modalTrainer: null,
      permissions: null,
      openedDropdowns: [],
      sourceTrainingSession: null,
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    currentExperiment: {
      get() {
        return this.$store.state.experiments.currentExperiment;
      },
      set(exp) {
        this.$store.commit('experiments/setCurrentExperiment', exp);
      },
    },
    currentSession: {
      get() {
        return this.$store.state.experiments.currentSession;
      },
      set(session) {
        this.$store.commit('experiments/setCurrentSession', session);
      },
    },
  },
  watch: {
    async currentExperiment(exp) {
      this.getTrainingSessions(exp.id);
    },
  },
  async created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
  },
  beforeUnmount() {
    this.stopInterval();
  },
  mounted() {
    const queryExpId = parseInt(this.$route.query.exp_id);
    if (!queryExpId && this.currentExperiment) {
      this.$router.replace({ name: this.$route.name, query: { exp_id: this.currentExperiment.id } });
    }

    if (this.currentExperiment) {
      this.getTrainingSessions(this.currentExperiment.id);
    }

    this.startInterval();
    this.getProjectAccessForSingleUser();
  },
  methods: {
    async getTrainingSessions(id, showLoading = true) {
      if (showLoading) this.isLoading = true;
      this.training_sessions = await this.getTrainingSessionList(id);
      if (showLoading) this.isLoading = false;
    },
    async getProjectAccessForSingleUser() {
      if (this.currentProject && this.$store.state.user?.user?.username) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentProject.id,
          username: this.$store.state.user.user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            } else if (data.error) {
              $toast.open({
                queue: true,
                dismissible: true,
                message: `Failed to retrieve project access: ${data.error.message}`,
                type: "error",
                position: "top",
              });
            }
          })
          .catch((e) => {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve project access: ${e}`,
              type: "error",
              position: "top",
            });
          });
      }
    },

    openConfirmStopTrainingSessionModal(session) {
      this.confirmMessage = `Are you sure you want to stop training session "${session.name}"?\n`;
      this.confirmMessageHeader = 'Stop Training Session';
      this.toDelete = session;
      this.buttonText = 'Stop';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleStopTrainingSessions([session.id]);
      };
    },
    openConfirmDeleteSessionModal(session) {
      this.confirmMessage = `Are you sure you want to move training session "${session.name}" to recycle bin? This will also move training artifacts and validate sessions to recycle bin.`;
      this.confirmMessageHeader = 'Remove Training Session';
      this.toDelete = session;
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleDeleteSession(session.id);
      };
    },
     openConfirmResumeSessionModal(session) {
      this.confirmMessage = `Are you sure you want to resume training session "${session.name}"?`;
      this.confirmMessageHeader = 'Resume Training Session';
      this.toDelete = session;
      this.buttonText = 'Resume';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleResumeSession(session.id);
      };
    },
    handlePauseClicked() {
    },
    async handleRefreshSession(session) {
      const index = this.training_sessions.findIndex((e) => e.id === session.id);
      if (index > -1) {
        this.training_sessions[index] = await this.getSession(session.id);
      }
    },
    startInterval() {
      this.interval = setInterval(() => {
        this.getTrainingSessions(this.currentExperiment.id, false);
      }, 30000);
    },
    stopInterval() {
      clearInterval(this.interval);
      this.interval = null;
    },
    async updateEC2InstanceStatus() {
      await this.dataConnect.updateEC2Status({
        trainer_ids: this.trainerIds,
      })
        .catch((e) => console.log(e));
    },
    async handleTrainerCreated() {
      this.getTrainingSessions(this.currentExperiment.id);
    },
    async getTrainingSessionList(trainer_id) {
      const resp = await this.dataConnect.getTrainingSessionList({ trainer_id, reverse: true })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training sessions: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return [];
      }

      if (resp.result) {
        resp.result.forEach((session) => {
          if (this.selectedTrainingSession && session.id === this.selectedTrainingSession.id) {
            this.selectedTrainingSession = session;
          }
        });
      }
      return resp.result;
    },
    async getSession(id) {
      const resp = await this.dataConnect.getTrainingSession({
        trainer_session_id: id,
      })
        .catch((e) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training session: ${e}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training session: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return null;
      }
      return resp.result;
    },
    openTrainerStartModal(session) {
      this.trainerToCreateSession = this.currentExperiment.id;
      this.sourceTrainingSession = session;
      this.$refs.startTrainerSessionModal.showModal();
    },
    async handleTrainingSessionCreated() {
      this.getTrainingSessions(this.currentExperiment.id);
    },
    // async handleSessionAnalyticsClick() {
    //   this.trainerToShowAnalytics = { trainer_id: expID };
    //   await this.$nextTick();
    //   this.$refs.trainingSessionAnalyticsModal.showModal();
    // },
    async launchTrainedModelCompare(trainer_ids) {
      this.trainerToShowAnalytics = { trainer_ids };
      await this.$nextTick();
      this.$refs.trainingSessionAnalyticsModal.showModal();
    },
    async handleStopTrainingSessions(ids) {
      await this.dataConnect.terminateTrainingSessions({
        session_ids: ids,
      })
        .then((data) => {
          this.updateEC2InstanceStatus();
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to stop training session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to stop training session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleDeleteSession(id) {
      await this.dataConnect.deleteTrainingSession({
        session_id: id,
      })
        .then(async (data) => {
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to delete training session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          } else if (data.result) {
            this.getTrainingSessions(this.currentExperiment.id);
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to delete training session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleResumeSession(id) {
      await this.dataConnect.resumeTrainingSession({
        session_id: id,
      })
        .then(async (data) => {
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to resume training session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          } else if (data.result) {
            this.getTrainingSessions(this.currentExperiment.id);
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to delete training session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleGetExperiment(id) {
      await this.dataConnect.getExperiment({ trainer_id: id })
        .then(async (data) => {
          if (data.result) {
            this.currentExperiment = data.result;
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
    handleInfoClicked(session) {
      this.currentSession = session;
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.training.details`, query: { train_session_id: session.id } });
    },
    handleSessionChartsClicked(session) {
      this.currentSession = session;
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.training.details`, query: { train_session_id: session.id, mode: 'charts' } });
    },
    handleShowAllChartsClick() {
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.training.charts`, query: { exp_id: this.currentExperiment.id } });
    },
  },
};
</script>

<style lang="scss" scoped>

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;

    &.experiment-header {
      flex-direction: column;
    }
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    width: fit-content
  }

  &__middle {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}
@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}
.trainers-card-container {
  display: flex;
  width: 100%;
  padding: 16px;
  overflow-y: scroll;
}

.trainer-header {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;

  &__message {
    position: absolute;
    display: flex;
    flex-direction: column;
    text-align: center;
    top: 60%;
    @include themify() {
      color: themed('body-text-color');
    }
  }
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }
</style>
